import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useLocationHandling } from '../LocationHandling/useLocationHandling';
import SidebarMain from '../../sidebars/SidebarMain';
import {PostSection} from "./GalleryUIFunctions";
import type {MediaEntry, Post} from "./GalleryUIFunctions";
import { useTranslation } from 'react-i18next';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import GalleryPresentation from './GalleryPresentation';

type LocLike = { city?: string; country?: string; countryCode?: string;};

// Api base URL normalization
const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

// ✅ bulletproof normalization
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

// ---- Asset prefetch utilities ----
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|m3u8|mpd)$/i;
const isVideoUrl = (u: string) => {
  try {
    const pathname = new URL(u, window.location.origin).pathname.toLowerCase();
    return VIDEO_EXT_RE.test(pathname);
  } catch {
    return VIDEO_EXT_RE.test(u);
  }
};

const IMAGE_BASE = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_BASE) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const toImageUrl = (p: string): string => {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const base = IMAGE_BASE.replace(/\/$/, '');
  const rel  = p.replace(/^\//, '');
  return `${base}/${rel}`;
};

// Idle scheduler so we don't block the main thread
const scheduleIdle = (fn: () => void) => {
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(fn, { timeout: 1000 });
  } else {
    setTimeout(fn, 0);
  }
};

// How aggressively we prefetch
const getPrefetchConfig = () => {
  const conn = (navigator as any)?.connection;
  const et = conn?.effectiveType;

  if (!conn) return { posts: 18, media: 8 };

  if (et === '4g') return { posts: 30, media: 10 };
  if (et === '3g') return { posts: 14, media: 6 };
  return { posts: 6, media: 3 };
};


const { posts: PREFETCH_POSTS_AHEAD, media: PREFETCH_MEDIA_PER_POST } =
  getPrefetchConfig();


const resolveHlsPrefetchUrl = (media: MediaEntry): string | null => {
  if (media.hlsUrl) return media.hlsUrl;
  if (media.videoId) return `${API_ORIGIN}/hls/playlist/${media.videoId}`;
  return null;
};

const prefetchHlsSegments = async (media: MediaEntry) => {
  try {
    const playlistUrl = resolveHlsPrefetchUrl(media);
    if (!playlistUrl) return;

    const res = await fetch(playlistUrl, { cache: 'force-cache' });
    const text = await res.text();

    const segmentUrls = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .slice(0, 3);

    segmentUrls.forEach(seg => {
      const abs = new URL(seg, playlistUrl).toString();
      fetch(abs, { cache: 'force-cache' }).catch(() => {});
    });
  } catch {}
};


const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';
const G_SCROLL = 'gallery:scrollTop';
const G_INDEX  = 'gallery:pageIndex';
const G_OFFSET = 'gallery:offset';
const G_POSTS  = 'gallery:posts';    // session snapshot (we'll cap size)
const SNAPSHOT_POST_LIMIT = 30;      // don’t blow up sessionStorage

// Limiting Memory
const WINDOW_BEFORE = 3;
const WINDOW_AFTER = 4;
const MAX_POSTS_IN_MEMORY = 120;


// Optional: keep image handles bounded to avoid unbounded memory
const MAX_IMAGE_HANDLES = 300;
const trimImageHandles = () => {
  if (liveImageHandles.size <= MAX_IMAGE_HANDLES) return;
  // drop oldest ~50
  const drop = Math.min(50, liveImageHandles.size);
  const it = liveImageHandles.keys();
  for (let i = 0; i < drop; i++) {
    const k = it.next().value;
    if (k) liveImageHandles.delete(k);
  }
};

//Impression measurement for Advertising
const firedImpressions = new Set<string>();

const TrackAdImpression: React.FC<{ adId: string; isActive: boolean }> = ({ adId, isActive }) => {
  useEffect(() => {
    if (!isActive) return;

    // 🚫 don't fire twice per session
    if (firedImpressions.has(adId)) return;

    const t = setTimeout(() => {
      firedImpressions.add(adId);

      api.post(`/ads/${adId}/impression`)
        .catch(err => console.error("❌ Impression tracking failed", err));
    }, 800);

    return () => clearTimeout(t);
  }, [isActive, adId]);

  return null;
};


// Light network heuristic: don't prefetch on very slow/expensive links
const canPrefetch = () => {
  const nav = (navigator as any);
  const conn = nav?.connection || nav?.mozConnection || nav?.webkitConnection;
  const saveData = !!conn?.saveData;
  const et: string | undefined = conn?.effectiveType;
  // treat '2g', 'slow-2g' as too slow
  const verySlow = et === 'slow-2g' || et === '2g';
  return !saveData && !verySlow;
};

// In-memory cache so we don't prefetch the same URL repeatedly
const prefetched = new Set<string>();

/**
 * Prefetch an image/video. For images we keep a reference to avoid premature GC.
 * For videos we create a metadata-only video element.
 */
const liveImageHandles = new Map<string, HTMLImageElement>();

const prefetchAsset = (url?: string) => {
  if (!url || prefetched.has(url) || !canPrefetch()) return;

  if (isVideoUrl(url)) {
    const v = document.createElement('video');
    v.preload = 'metadata';        // keep it light
    v.src = url;
    // Kick load on iOS/Safari
    try { v.load(); } catch {}
  } else {
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
    liveImageHandles.set(url, img);
    // When it settles, we can drop the handle
    const cleanup = () => liveImageHandles.delete(url);
    img.onload = cleanup;
    img.onerror = cleanup;
  }

  // Optional <link rel="prefetch"> to hint the browser (mostly for images)
  try {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
    // don't bother tracking/removing; browsers ignore duplicates
  } catch {}

  prefetched.add(url);
};

const Gallery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { location } = useLocationHandling();
  const locCountryCode = (location as any)?.countryCode as string | undefined;
  const [likingInProgress, setLikingInProgress] = useState<string | null>(null);
  const [offset, setOffset] = useState<number>(() => {
    const v = Number(sessionStorage.getItem(G_OFFSET));
    return Number.isFinite(v) ? v : 0;
  });
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 15; // You can tune this for performance
  const { likedIds, likeCounts, likePost, unlikePost, refreshLikes } = useLikes();

 const [posts, setPosts] = useState<Post[]>(() => {
  try {
    // 🔥 instant memory restore (primary)
    const mem = sessionStorage.getItem("feed-memory-v1");
    if (mem) return JSON.parse(mem);

    // fallback snapshot
    const raw = sessionStorage.getItem(G_POSTS);
    const snap = raw ? JSON.parse(raw) : null;
    return Array.isArray(snap) ? snap : [];
  } catch {
    return [];
  }
});

const [isFetching, setIsFetching] = useState(false);
const [searchQuery, setSearchQuery] = useState("");
  
  
// which post has its music pill pinned (null = none)
const [musicFor, setMusicFor] = useState<string | null>(null);

const [presentationPost, setPresentationPost] = useState<Post | null>(null);
const [presentationIndex, setPresentationIndex] = useState<number | null>(null);

const [snapDisabled, setSnapDisabled] = useState<boolean>(false);
const rrLoc = useRouterLocation();

const [initialMediaReady, setInitialMediaReady] = useState(false);

//Loading Helper
const loadingLocationLabel = React.useMemo(() => {
  if (location?.city && location?.country) {
    return `${location.city}, ${location.country}`;
  }
  if (location?.country) return location.country;
  return 'your location';
}, [location]);


//Search Filter Helper
// Normalize/prepare a string for search
const normText = (s?: string | null) => (s || '').toLowerCase();

// Does this post match the current search?
const matchesSearch = (post: Post, rawQuery: string): boolean => {
  const q = rawQuery.trim();
  if (!q) return true; // empty search → no filtering

  // remove leading '#' so both "#budapest" and "budapest" work
  const needle = q.replace(/^#/, '').toLowerCase();

  // Collect searchable fields
  const haystack: string[] = [];

  // Caption / description fields (adjust names if needed)
  if ((post as any).caption) haystack.push((post as any).caption);
  if ((post as any).description) haystack.push((post as any).description);

  // Hashtags stored as an array? e.g. ["budapest", "nightlife"]
  if (Array.isArray((post as any).hashtags)) {
    haystack.push(...(post as any).hashtags);
  }
  if (Array.isArray((post as any).tags)) {
    haystack.push(...(post as any).tags);
  }

  // Also allow city / country to match
  if (post.location?.city) haystack.push(post.location.city);
  if (post.location?.country) haystack.push(post.location.country);

  // Substring, case-insensitive: "pest" matches "Budapest"
  return haystack.some((txt) =>
    txt && normText(txt).includes(needle)
  );
};

// in Gallery component, near the top of the function, BEFORE useMediaViewerOverlay:
const handleOpenMusic = async (entry: MediaEntry) => {
  // 1️⃣ Best case: we already stored a canonical track id
  if (entry.musicId) {
    navigate(`/musicdetails/${entry.musicId}`);
    return;
  }

  // 2️⃣ Fallback: search by title OR artist (not "title artist")
  const q = (entry.musicTitle || entry.musicArtist || '').trim();
  if (!q) {
    console.warn('[handleOpenMusic] no musicTitle/musicArtist on entry', entry);
    return;
  }

  try {
    const res = await api.get('/music', { params: { q } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);

    if (!list.length) {
      console.warn('[handleOpenMusic] no matching track for query', q, entry);
      return;
    }

    navigate(`/musicdetails/${list[0]._id}`);
  } catch (e) {
    console.error('[handleOpenMusic] Music lookup failed:', e);
  }
};

const filteredPosts = React.useMemo(
  () => posts.filter((p) => matchesSearch(p, searchQuery)),
  [posts, searchQuery]
);
 
const openPresentationAt = (post: Post, mediaIndex: number) => {
  // remember scroll restore anchor
  sessionStorage.setItem('galleryRestorePostId', post._id);

  setPresentationPost(post);
  setPresentationIndex(mediaIndex);
};


// Save current scroll before leaving the gallery
const openPresentation = (postId: string) => {
  sessionStorage.setItem('galleryRestorePostId', postId);

  const post = posts.find(p => p._id === postId);
  if (!post) return;

  setPresentationPost(post);
  setPresentationIndex(0); // 👈 default to first media
};


const saveGallerySnapshot = () => {
  try {
    const top = feedRef.current?.scrollTop ?? 0;
    sessionStorage.setItem(G_SCROLL, String(top));
    sessionStorage.setItem(G_INDEX, String(pageIndex));
    sessionStorage.setItem(G_OFFSET, String(offset));

    const snap = posts.slice(0, SNAPSHOT_POST_LIMIT);
    sessionStorage.setItem(G_POSTS, JSON.stringify(snap));

    const anchorId = filteredPosts[pageIndex]?._id;
    if (anchorId) sessionStorage.setItem('gallery:anchorId', anchorId);
  } catch {}
};

const feedRef = useRef<HTMLDivElement | null>(null);
  
const getCacheKey = (loc: LocLike) => {
  const valid = isValidPlace(loc);
  if (!valid) return 'prefetchedMediaPosts:GLOBAL';
  const cc = (loc.countryCode || '').toUpperCase();
  return `prefetchedMediaPosts:${loc.city || ''}:${cc || loc.country || ''}`;
};

//Sorting
const createdAtTime = (p: { createdAt: string }) => {
  const t = Date.parse(p.createdAt);
  return Number.isFinite(t) ? t : 0;
};

const sortByDateDesc = (a: { createdAt: string }, b: { createdAt: string }) =>
  createdAtTime(b) - createdAtTime(a);

const norm = (s?: string) => (s || '').trim().toLowerCase();

const isValidPlace = (loc?: LocLike) => {
  if (!loc) return false;
  const cityOk    = !!loc.city && loc.city !== 'Unknown';
  const countryOk = !!loc.country && loc.country !== 'Unknown';   // ← add this check
  const codeOk    = !!loc.countryCode;
  return cityOk || countryOk || codeOk;
};

function rankForPost(p: Post, base: LocLike): 3 | 2 | 1 {
  const city = norm(p.location?.city);
  const country = norm(p.location?.country);
  const code = (p.location?.countryCode || '').toUpperCase();

  const baseCity = norm(base.city);
  const baseCountry = norm(base.country);
  const baseCode = (base.countryCode || '').toUpperCase();

  if (
    city &&
    baseCity &&
    city === baseCity &&
    (
      country === baseCountry ||
      (baseCode && code === baseCode)
    )
  ) {
    return 3;
  }

  if (
    (baseCode && code === baseCode) ||
    (country && baseCountry && country === baseCountry)
  ) {
    return 2;
  }

  return 1;
}

const sortWithPlaceBoost = (arr: Post[], base?: LocLike) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];

  const hasAds = arr.some((p: any) => p && (p as any).isAd);
  if (hasAds) {
    return arr.slice();
  }

  if (!isValidPlace(base)) {
    return arr.slice().sort(sortByDateDesc);
  }

  return arr.slice().sort((a, b) => {
    const ra = rankForPost(a, base!);
    const rb = rankForPost(b, base!);

    if (ra !== rb) {
      return rb - ra;
    }

    return sortByDateDesc(a, b);
  });
};

  const buildMediaParams = (loc: LocLike, limit?: number, offset?: number) => {
    const params: Record<string, any> = {};
    if (limit != null)  params.limit = limit;
    if (offset != null) params.offset = offset;
  
    if (loc.city && loc.city !== 'Unknown') params.city = loc.city;
  
    // ✅ Prefer ISO code; fallback to country name
    if (loc.countryCode) params.countryCode = loc.countryCode;
    else if (loc.country && loc.country !== 'Unknown') params.country = loc.country;
  
    return params;
  };

const asMediaEntry = (m: any): MediaEntry | null => {
  if (!m) return null;

  const isVideoLike =
    m.isHls === true ||
    !!m.videoId ||
    !!m.hlsUrl ||
    !!m.dashUrl;

  if (isVideoLike) {
    return {
      isHls: !!m.isHls || !!m.videoId || !!m.hlsUrl,
      videoId: typeof m.videoId === 'string' ? m.videoId : null,
      hlsUrl: m.hlsUrl ?? null,
      dashUrl: m.dashUrl ?? null,
      posterUrl: m.posterUrl ?? null,
      url: typeof m.url === 'string' ? m.url : undefined,
      musicId: m.musicId ?? null,
      musicTitle: m.musicTitle ?? null,
      musicArtist: m.musicArtist ?? null,
    };
  }

  const raw = m.url ?? m.videoUrl ?? m.src ?? m.path ?? m.file ?? null;
  if (!raw || typeof raw !== 'string') return null;

  return {
    url: toImageUrl(raw),
    musicId: m.musicId ?? m.music?._id ?? m.audio?._id ?? null,
    musicTitle:
      m.musicTitle ??
      m.songTitle ??
      m.trackTitle ??
      m.music?.title ??
      m.audio?.title ??
      null,
    musicArtist:
      m.musicArtist ??
      m.songArtist ??
      m.trackArtist ??
      m.music?.artist ??
      m.audio?.artist ??
      null,
  };
};


const formatMediaArray = (arr: any[]): MediaEntry[] =>
  (Array.isArray(arr) ? arr : []).map(asMediaEntry).filter(Boolean) as MediaEntry[];

const [globalMuted, setGlobalMuted] = useState<boolean>(true); // start muted

const getMediaUrl = toImageUrl;

const [pageIndex, setPageIndex] = useState<number>(() => {
  const v = Number(sessionStorage.getItem(G_INDEX));
  return Number.isFinite(v) ? v : 0;
});

useEffect(() => {
  console.log('Current page index:', pageIndex);
}, [pageIndex]);

// Prefetch should ONLY warm cache – do NOT setPosts here
const warmMediaCache = async () => {
  try {
    const params = buildMediaParams(location, 4, 0);
    const res = await api.get('/media', { params });

    const data = Array.isArray(res.data?.posts) ? res.data.posts
               : Array.isArray(res.data) ? res.data : [];

               const normalized = data.map((p: any) => {
                const likeKey = likeKeyFromRow(p);
                return {
                  ...p,
                  _id: p._id,
                  likeKey,                                  // 👈 ensure cache has it too
                  media: formatMediaArray(p.media),
                  user: p.user
                    ? { ...p.user, picture: p.user?.picture ? getMediaUrl(p.user.picture) : null }
                    : null,
                };
              });
              

    const sorted = sortWithPlaceBoost(normalized as Post[], {
      city: location.city,
      country: location.country,
      countryCode: locCountryCode || location.countryCode,
    });

    const key = getCacheKey({
      city: location.city,
      country: location.country,
      countryCode: locCountryCode || (location as any).countryCode,
    });

    if (sorted.length) {
      localStorage.setItem(key, JSON.stringify({ value: sorted, expiry: Date.now() + 30_000 }));
    }
  } catch (err) {
    console.error('❌ warmMediaCache failed:', err);
  }
};

const anchoredOnceRef = React.useRef(false);

//Refresh Likes
useEffect(() => {
  if (likedIds.size === 0) refreshLikes();
}, []);


useEffect(() => {
  if (!posts.length) {
    setInitialMediaReady(false);
    return;
  }

  const firstPost = posts[0];
  const media = firstPost?.media?.[0];

  let cancelled = false;

  const finish = () => {
    if (cancelled) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          setInitialMediaReady(true);
        }
      });
    });
  };

  if (!media) {
    finish();
    return () => {
      cancelled = true;
    };
  }

  // IMAGE
  if (media.url && !isVideoUrl(media.url)) {
    const img = new Image();
    img.src = media.url;

    const ready = async () => {
      try {
        await img.decode();
      } catch {}

      finish();
    };

    if (img.complete) {
      ready();
    } else {
      img.onload = ready;
      img.onerror = finish;
    }

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }

  // VIDEO WITH POSTER
  if (media.posterUrl) {
    const poster = new Image();
    poster.src = media.posterUrl;

    const ready = async () => {
      try {
        await poster.decode();
      } catch {}

      finish();
    };

    if (poster.complete) {
      ready();
    } else {
      poster.onload = ready;
      poster.onerror = finish;
    }

    return () => {
      cancelled = true;
      poster.onload = null;
      poster.onerror = null;
    };
  }

  // VIDEO WITHOUT POSTER
  finish();

  return () => {
    cancelled = true;
  };
}, [posts]);


// Keyboard: swipe one post per key press (Down/Up, PgDn/PgUp, J/K, Space)
useEffect(() => {
  const el = feedRef.current;
  if (!el) return;

  const isTypingTarget = (t: EventTarget | null) => {
    const n = t as HTMLElement | null;
    if (!n) return false;
    const tag = n.tagName;
    return (
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      (n as HTMLElement).isContentEditable
    );
  };

  const getIndex = () =>
    Math.round((el.scrollTop || 0) / (el.clientHeight || 1));

  const scrollToIndex = (i: number) => {
    const max = Math.max(0, filteredPosts.length - 1);
    const idx = Math.max(0, Math.min(max, i));
    el.scrollTo({ top: idx * (el.clientHeight || 1), behavior: 'smooth' });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    // don't hijack typing in fields or editable areas
    if (isTypingTarget(e.target)) return;

    // if no posts, ignore
    if (!filteredPosts.length) return;

    const idx = getIndex();

    // Down-like keys: ArrowDown, PageDown, J/j, Space (unless Shift+Space)
    if (
      e.key === 'ArrowDown' ||
      e.key === 'PageDown' ||
      e.key === 'j' ||
      e.key === 'J' ||
      (e.key === ' ' && !e.shiftKey)
    ) {
      e.preventDefault();
      scrollToIndex(idx + 1);
      return;
    }

    // Up-like keys: ArrowUp, PageUp, K/k, Shift+Space
    if (
      e.key === 'ArrowUp' ||
      e.key === 'PageUp' ||
      e.key === 'k' ||
      e.key === 'K' ||
      (e.key === ' ' && e.shiftKey)
    ) {
      e.preventDefault();
      scrollToIndex(idx - 1);
      return;
    }

    // Home / End jump
    if (e.key === 'Home') {
      e.preventDefault();
      scrollToIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      scrollToIndex(filteredPosts.length - 1);
      return;
    }
  };

  window.addEventListener('keydown', onKeyDown, { passive: false });
  return () => window.removeEventListener('keydown', onKeyDown);
}, [filteredPosts.length]);


useEffect(() => {
  if (anchoredOnceRef.current) return;
  const postId = sessionStorage.getItem('galleryRestorePostId');
  if (!postId || !feedRef.current) return;

  const container = feedRef.current;

  // try immediate
  const tryAnchor = () => {
    const el = container.querySelector<HTMLElement>(`[data-post-id="${postId}"]`);
    if (!el) return false;
    // Let the browser scroll the nearest scrollable ancestor (the container)
    // Scroll-snap will naturally align it to the page without us disabling anything.
    el.scrollIntoView({ block: 'start', inline: 'nearest' }); // behavior defaults to 'auto'
    anchoredOnceRef.current = true;
    sessionStorage.removeItem('galleryRestorePostId');
    return true;
  };

  if (tryAnchor()) return;

  // Retry briefly while items paginate/render in
  let attempts = 0;
  const t = setInterval(() => {
    attempts += 1;
    if (tryAnchor() || attempts >= 20) clearInterval(t); // ~2s @ 100ms
  }, 100);
  return () => clearInterval(t);
}, [posts]); // depend on posts so we retry as they load

// Prefetch a window of upcoming posts (5 ahead by default)
useEffect(() => {
  if (!canPrefetch()) return;
  if (!filteredPosts.length) return;

  const start = Math.max(0, pageIndex - 2);
  const end = Math.min(
    filteredPosts.length,
    pageIndex + 1 + PREFETCH_POSTS_AHEAD
  );

  const queue: string[] = [];

  for (let i = start; i < end; i++) {
    const post = filteredPosts[i];
    if (!post) continue;

    // 👤 author avatar
    if (post.user?.picture) queue.push(post.user.picture);

    // 🎞 media
    const mediaArr = post.media || [];
    const N = Math.min(PREFETCH_MEDIA_PER_POST, mediaArr.length);

    for (let j = 0; j < N; j++) {
      const media = mediaArr[j];

      // 🔥 HLS video → prefetch playlist
      if (media?.isHls || media?.hlsUrl || media?.videoId) {
  prefetchHlsSegments(media);
  continue;
}

      // 🖼 image / mp4
      if (media?.url) {
        queue.push(media.url);
      }
    }
  }

 scheduleIdle(() => {
  Promise.allSettled(queue.slice(0, 20).map(prefetchAsset));
  trimImageHandles();
});

}, [pageIndex, filteredPosts]);


useEffect(() => {
  if (!canPrefetch()) return;

  const next = filteredPosts[pageIndex + 1];
  if (!next) return;

  scheduleIdle(() => {
    next.media?.forEach(m => {
      if (m.isHls || m.hlsUrl || m.videoId) prefetchHlsSegments(m);
      if (m.url) prefetchAsset(m.url);
    });
  });
}, [pageIndex]);

useEffect(() => {
  const origins = [API_ORIGIN, IMAGE_BASE];

  origins.forEach(o => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = o;
    document.head.appendChild(link);
  });
}, []);


// Warm-up when list first appears
useEffect(() => {
  if (!canPrefetch()) return;
  if (!filteredPosts.length) return;

  const firstWindow = Math.min(
    filteredPosts.length,
    PREFETCH_POSTS_AHEAD
  );

  const queue: string[] = [];

  for (let i = 0; i < firstWindow; i++) {
    const post = filteredPosts[i];
    if (!post) continue;

    // 👤 avatar
    if (post.user?.picture) queue.push(post.user.picture);

    // 🎞 media
    const mediaArr = post.media || [];
    const N = Math.min(PREFETCH_MEDIA_PER_POST, mediaArr.length);

    for (let j = 0; j < N; j++) {
      const media = mediaArr[j];

      // 🔥 HLS video → prefetch playlist
      if (media?.isHls || media?.hlsUrl || media?.videoId) {
  prefetchHlsSegments(media);
  continue;
}

      // 🖼 image / mp4
      if (media?.url) {
        queue.push(media.url);
      }
    }
  }

scheduleIdle(() => {
  Promise.allSettled(queue.slice(0, 20).map(prefetchAsset));
  trimImageHandles();
});

}, [filteredPosts.length]);


useEffect(() => {
  if (!hasMore) return;
  if (isFetching) return;

  // if we already have enough posts ahead, do nothing
  const postsAhead = posts.length - pageIndex;

  if (postsAhead > 15) return;

  // preload next page silently
  fetchPosts(location.city, location.country, offset);
}, [pageIndex, posts.length, hasMore, isFetching, offset]);


// call once on mount / location change
useEffect(() => {
  const params = new URLSearchParams(rrLoc.search);
  const hasRefreshParam = params.has('refresh');
  const hardReloadFlag = sessionStorage.getItem(GALLERY_HARD_RELOAD_KEY) === '1';

  const shouldHardReload = hardReloadFlag || hasRefreshParam;

  if (shouldHardReload) {
    sessionStorage.removeItem(GALLERY_HARD_RELOAD_KEY);
    sessionStorage.removeItem(G_SCROLL);
    sessionStorage.removeItem(G_INDEX);
    sessionStorage.removeItem(G_OFFSET);
    sessionStorage.removeItem(G_POSTS);
    sessionStorage.removeItem('gallery:anchorId');
    sessionStorage.removeItem('galleryRestorePostId');

    setPageIndex(0);
    setOffset(0);
    setHasMore(true);
    setPosts([]);
    setIsFetching(false); // optional extra safety
    fetchPosts(location.city, location.country, 0, true);
  } else {
    if (!posts.length && !isFetching) {
  setHasMore(true);
  fetchPosts(location.city, location.country, 0);
} else {
  warmMediaCache(); // only when feed already visible
}

  }

  const id = setInterval(warmMediaCache, 30_000);
  return () => clearInterval(id);
}, [location.city, location.country, locCountryCode, rrLoc.search]);


// 2a) Only one <video> plays at a time
useEffect(() => {
  const handlePlay = (e: Event) => {
    const target = e.target as HTMLVideoElement | null;
    if (!target || target.tagName !== 'VIDEO') return;
    document.querySelectorAll('video').forEach((v) => {
      if (v !== target) {
        try { v.pause(); } catch {/* ignore */}
      }
    });
  };
  document.addEventListener('play', handlePlay, true); // capture phase
  return () => document.removeEventListener('play', handlePlay, true);
}, []);


useEffect(() => {
  if (!posts.length) return;
  if (!hasMore) return;

  // preload next pages once feed becomes visible
  const t1 = setTimeout(() => {
    fetchPosts(location.city, location.country, offset);
  }, 1200);

  const t2 = setTimeout(() => {
    fetchPosts(location.city, location.country, offset + LIMIT);
  }, 2400);

  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
  };
}, [posts.length]);

useEffect(() => {
  if (!posts.length) return;
  sessionStorage.setItem("feed-memory-v1", JSON.stringify(posts.slice(0, 40)));
}, [posts.length]);


// Search effect
useEffect(() => {
  // whenever the search changes, jump back to the top of the filtered list
  setPageIndex(0);
  if (feedRef.current) {
    feedRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }
}, [searchQuery]);


  // Fetch Posts and location   
  const fetchPosts = async (city: string, country: string, startOffset = 0, force = false) => {
if (!force && (isFetching || !hasMore)) return;
  setIsFetching(true);
  try {
    const params = buildMediaParams({ city, country, countryCode: locCountryCode }, LIMIT, startOffset);
    const res = await api.get('/media', { params });
    const data = res.data.posts || res.data;

    const transformed: Post[] = data.map((row: any) => {
      const likeKey = likeKeyFromRow(row);  // canonical id
    
      return {
        ...row,
        _id: row._id,        // keep original Mongo id
        likeKey,             // 👈 add this
        media: formatMediaArray(row.media),
        likedByUser:
          user && user._id && Array.isArray(row.likesBy)
            ? row.likesBy.some((id: any) => id.toString() === user._id)
            : false,
        user: row.user
          ? {
              _id: row.user?._id || '',
              profilename: row.user?.profilename || t('gallery.unknownUser'),
              picture: row.user?.picture ? toImageUrl(row.user.picture) : null
            }
          : null,
        location: {
          city: row.location?.city || t('gallery.unknownLocation'),
          country: row.location?.country || t('gallery.unknownLocation'),
          countryCode: row.location?.countryCode,
        },
      };
    });

    const sorted = sortWithPlaceBoost(transformed, {
      city,
      country,
      countryCode: locCountryCode,
    });

setPosts(prev => {
  if (startOffset === 0) {
    if (!prev.length) {
      return transformed.slice(-MAX_POSTS_IN_MEMORY);
    }

    const existingIds = new Set(prev.map(p => p._id));
    const newOnes = sorted.filter(p => !existingIds.has(p._id));
    const merged = [...prev, ...newOnes];

    return merged.slice(-MAX_POSTS_IN_MEMORY);
  }

  const existingIds = new Set(prev.map(p => p._id));
  const newOnes = sorted.filter(p => !existingIds.has(p._id));
  const merged = [...prev, ...newOnes];

  return merged.slice(-MAX_POSTS_IN_MEMORY);
});
    
    // 🚀 aggressive first preload
setOffset(startOffset + LIMIT);
const more = res.data.hasMore !== false;
setHasMore(more);

// 🚀 aggressive first preload (safe)
if (startOffset === 0 && more) {
  fetchPosts(city, country, LIMIT);
}

  } catch (err) {
    console.error('Failed to fetch paginated posts:', err);
  } finally {
    setIsFetching(false);
  }
};

// keep a permanent buffer of posts ahead of the user
useEffect(() => {
  if (!hasMore) return;
  if (isFetching) return;

  const BUFFER_TARGET = 24; // number of posts ahead always ready
  const postsAhead = posts.length - pageIndex - 1;

  if (postsAhead >= BUFFER_TARGET) return;

  // aggressively fetch next pages until buffer filled
  fetchPosts(location.city, location.country, offset);
}, [posts.length, pageIndex, hasMore, isFetching, offset]);


   // LIKE
   const handleLike = async (postKey: string) => {
    const id = String(postKey);
    if (likingInProgress === id) return;
    setLikingInProgress(id);
  
    try {
      const newLikes = await likePost(id);
  
      if (newLikes === undefined) {
        setPosts(prev =>
          prev.map(p => {
            const key = String((p as any).likeKey || p._id);
            return key === id ? { ...p, likedByUser: true } : p;
          })
        );
        return;
      }
  
      setPosts(prev =>
        prev.map(p => {
          const key = String((p as any).likeKey || p._id);
          return key === id ? { ...p, likedByUser: true, likes: newLikes } : p;
        })
      );
    } catch (err) {
      console.error('Like failed:', err);
    } finally {
      setLikingInProgress(null);
    }
  };
  
  const handleUnlike = async (postKey: string) => {
    const id = String(postKey);
    if (likingInProgress === id) return;
    setLikingInProgress(id);
  
    try {
      const newLikes = await unlikePost(id);
  
      if (newLikes === undefined) {
        setPosts(prev =>
          prev.map(p => {
            const key = String((p as any).likeKey || p._id);
            return key === id ? { ...p, likedByUser: false } : p;
          })
        );
        return;
      }
  
      setPosts(prev =>
        prev.map(p => {
          const key = String((p as any).likeKey || p._id);
          return key === id ? { ...p, likedByUser: false, likes: newLikes } : p;
        })
      );
    } catch (err) {
      console.error('Unlike failed:', err);
    } finally {
      setLikingInProgress(null);
    }
  };
  

//Mute
useEffect(() => {
  const saved = localStorage.getItem('globalMuted');
  if (saved != null) setGlobalMuted(JSON.parse(saved));
}, []);
useEffect(() => {
  localStorage.setItem('globalMuted', JSON.stringify(globalMuted));
}, [globalMuted]);


useEffect(() => {
  return () => {
    saveGallerySnapshot(); // when leaving Gallery
  };
}, [pageIndex, offset, posts]);

// Restore once posts are present
useEffect(() => {
  if (sessionStorage.getItem(GALLERY_HARD_RELOAD_KEY) === '1') return; // cold path skips

  const el = feedRef.current;
  if (!el) return;
  if (!posts.length) return;

  const anchorId = sessionStorage.getItem('gallery:anchorId');
  const topStr   = sessionStorage.getItem(G_SCROLL);

  let attempts = 0;
  setSnapDisabled(true);

  const tryRestore = () => {
    attempts += 1;

    // Prefer anchor id (more robust than raw scrollTop)
    if (anchorId) {
      const target = el.querySelector<HTMLElement>(`[data-post-id="${anchorId}"]`);
      if (target) {
        target.scrollIntoView({ block: 'start', inline: 'nearest' });
        // leave snap disabled for one more frame to avoid snap interference
        requestAnimationFrame(() => setSnapDisabled(false));
        return;
      }
    }

    // Fallback: absolute pixel restore
    if (topStr) {
      el.scrollTo({ top: Number(topStr), behavior: 'auto' });
      requestAnimationFrame(() => setSnapDisabled(false));
      return;
    }

    // Retry a few times while images/layout settle
    if (attempts < 15) {
      setTimeout(tryRestore, 50);
    } else {
      setSnapDisabled(false);
    }
  };

  // kick off after one frame so children are measured
  requestAnimationFrame(tryRestore);
}, [posts.length]);


useEffect(() => {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
}, []);


return (
  <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
    
    {/* Sidebar */}
 <SidebarMain
  searchQuery={searchQuery}
  onSearchChange={setSearchQuery}
/>

{/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>

    
      {/* Feed Content — vertical swipe per post + horizontal carousel inside */}
<div
  ref={feedRef}
  onScroll={(e) => {
    const el = e.currentTarget as HTMLDivElement;
    const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
    if (idx !== pageIndex) setPageIndex(idx);
  
    // NEW: remember the post id we’re centered on
    const anchorId = filteredPosts[idx]?._id;
    if (anchorId) sessionStorage.setItem('gallery:anchorId', anchorId);
  }}
  style={{
  ...feedContainerStyle,
  position: "relative",
  padding: "7px 7px 0 7px",
  height: "100vh",
  scrollSnapType: snapDisabled ? "none" : "y mandatory",
  touchAction: "pan-y",
  overscrollBehaviorY: "contain",
}}
>
{/* POSTS ALWAYS RENDER */}
{filteredPosts.length > 0 ? (
  filteredPosts.map((post, idx) => {
    const isActive = idx === pageIndex;
    const inWindow =
      idx >= pageIndex - WINDOW_BEFORE &&
      idx <= pageIndex + WINDOW_AFTER;

    const shouldRender = inWindow;

    const handleAdClick = () => {
      if (post.isAd) {
        api.post(`/ads/${post._id}/click`).catch((err) =>
          console.error("❌ Click tracking failed", err)
        );
      }
    };

    if (!shouldRender) {
      return (
        <section
          key={post._id}
          data-post-id={post._id}
          style={{
            minHeight: "100vh",
            height: "100vh",
            scrollSnapAlign: "start",
            scrollSnapStop: "always",
          }}
        />
      );
    }

    return (
      <section
        key={post._id}
        data-post-id={post._id}
        style={{
          minHeight: "100vh",
          height: "100vh",
          scrollSnapAlign: "start",
          scrollSnapStop: "always",
        }}
      >
        {post.isAd && (
          <TrackAdImpression
            adId={post._id}
            isActive={isActive}
          />
        )}

        <PostSection
          post={post}
          isActive={isActive}
          inWindow={inWindow}
          globalMuted={globalMuted}
          openViewer={(_, i) => openPresentationAt(post, i)}
          navigate={navigate}
          onLike={() => handleLike(post.likeKey || post._id)}
          onUnlike={() => handleUnlike(post.likeKey || post._id)}
          currentUserId={user?._id}
          openPresentation={openPresentation}
          onMusicClick={handleOpenMusic}
          onAdClick={handleAdClick}
          likedIds={likedIds}
          likeCounts={likeCounts}
        />
      </section>
    );
  })
) : !isFetching ? (
  <p style={{ color: "#bbb", padding: 16 }}>
    {t("gallery.noPostsForFilter")}
  </p>
) : null}

{!initialMediaReady && (
  <div
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 100,
      backgroundColor: "#000",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 12,
      color: "#bbb",
      fontSize: 18,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        border: "3px solid rgba(255,255,255,0.15)",
        borderTop: "3px solid #fff",
        animation: "spin 0.9s linear infinite",
      }}
    />

    <div>
      Loading posts in <b>{loadingLocationLabel}</b>…
    </div>
  </div>
)}

</div>
{presentationPost && presentationIndex !== null && (
  <GalleryPresentation
    post={presentationPost}
    startIndex={presentationIndex}
    onClose={() => {
      setPresentationPost(null);
      setPresentationIndex(null);
    }}
  />
)}
</div>
); 
};

// Styles
const SIDEBAR_WIDTH = 270;    

const feedContainerStyle: React.CSSProperties = {
  flexGrow: 1,
  padding: '0px 20px', 
  overflowY: 'auto',
  height: '100vh',
  minHeight: '100%', // ✅ Add this line
  marginLeft: `${SIDEBAR_WIDTH}px`,
  backgroundColor: '#000',
  border: 'none',
  touchAction: 'pan-y',
  overscrollBehaviorY: 'contain',
  overscrollBehaviorX: 'none',
  overflowAnchor: 'none',
};


export default Gallery;